import { useEffect, useState } from "react";
import { NavLink, Outlet, Navigate, useNavigate, useLocation } from "react-router-dom";
import { Home, Clapperboard, Film, Clock, Lock, LogOut, Moon, Zap, Gamepad2 } from "lucide-react";
import { Logo, KidAvatar } from "./Brand";
import { ParentGate } from "./ParentGate";
import { useAuth } from "../context/AuthContext";
import { kidApi } from "../lib/api";

const links = [
  { to: "/kid", label: "Explore", icon: Home, end: true, id: "nav-explore" },
  { to: "/kid/shorts", label: "Shorts", icon: Zap, id: "nav-shorts" },
  { to: "/kid/games", label: "Games", icon: Gamepad2, id: "nav-games" },
  { to: "/kid/studio", label: "Studio", icon: Clapperboard, id: "nav-studio" },
  { to: "/kid/mine", label: "Mine", icon: Film, id: "nav-my-videos" },
];

export default function KidLayout() {
  const { kid, setKid, logoutKid } = useAuth();
  const [left, setLeft] = useState(null);
  const [gate, setGate] = useState(false);
  const nav = useNavigate();
  const onGames = useLocation().pathname.startsWith("/kid/games");

  useEffect(() => {
    if (!kid) return;
    kidApi.get("/kid/me").then((r) => { setKid(r.data); setLeft(r.data.minutes_left); }).catch(() => {});
    const beat = () => document.visibilityState === "visible" && !window.location.pathname.startsWith("/kid/games") && kidApi.post("/kid/heartbeat").then((r) => setLeft(r.data.minutes_left)).catch(() => {});
    const iv = setInterval(beat, 60000);
    const onUp = () => setLeft(0);
    window.addEventListener("zunii:timeup", onUp);
    return () => { clearInterval(iv); window.removeEventListener("zunii:timeup", onUp); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kid?.id]);

  if (kid === undefined) return <div className="min-h-screen grid place-items-center font-display text-2xl">Loading Zunii…</div>;
  if (!kid) return <Navigate to="/profiles" replace />;
  const switchProfile = () => { logoutKid(); nav("/profiles"); };

  return (
    <div className="min-h-screen bg-[#FFF7EC]">
      <header className="sticky top-0 z-30 bg-[#FFF7EC]/90 backdrop-blur-md border-b-[3px] border-[#1B1A2E] pt-[env(safe-area-inset-top)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 sm:h-20 flex items-center gap-3 sm:gap-4">
          <Logo className="text-xl sm:text-2xl" />
          <nav className="hidden md:flex gap-2 ml-6">
            {links.map(({ to, label, icon: Icon, end, id }) => (
              <NavLink key={to} to={to} end={end} data-testid={id}
                className={({ isActive }) => `zn-chip ${isActive ? "bg-[#FFD23F]" : "bg-white"}`}>
                <Icon size={16} /> {label}
              </NavLink>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-2 sm:gap-3">
            <span data-testid="screen-time-badge" className={`zn-chip !px-3 sm:!px-4 ${left !== null && left <= 5 ? "bg-[#FF6B4A] text-white" : "bg-[#B8F2C9]"}`}>
              <Clock size={16} /> {left === null ? "…" : <><span className="sm:hidden">{left}m</span><span className="hidden sm:inline">{left} min left</span></>}
            </span>
            <button data-testid="switch-profile-btn" onClick={switchProfile} className="flex items-center gap-2 group" title="Switch profile">
              <KidAvatar avatar={kid.avatar} color={kid.color} frame={kid.frame} size={44} className="group-hover:-rotate-6 transition-transform" />
              <span className="hidden sm:block font-display font-semibold">{kid.name}</span>
            </button>
            <button data-testid="open-parent-gate-btn" onClick={() => setGate(true)} className="w-11 h-11 grid place-items-center rounded-full border-[3px] border-[#1B1A2E] bg-white hover:bg-[#FFD23F] transition-colors" title="Grown-ups">
              <Lock size={18} />
            </button>
          </div>
        </div>
      </header>
        <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t-[3px] border-[#1B1A2E] grid grid-cols-5 pb-[env(safe-area-inset-bottom)]" data-testid="kid-bottom-nav">
          {links.map(({ to, label, icon: Icon, end, id }) => (
            <NavLink key={to} to={to} end={end} data-testid={`${id}-mobile`}
              className={({ isActive }) => `flex flex-col items-center gap-1 py-2.5 font-display text-xs font-semibold transition-colors ${isActive ? "text-[#1B1A2E]" : "text-[#1B1A2E]/50"}`}>
              {({ isActive }) => (<><span className={`px-3 py-1 rounded-full border-2 ${isActive ? "bg-[#FFD23F] border-[#1B1A2E]" : "border-transparent"}`}><Icon size={22} /></span>{label}</>)}
            </NavLink>
          ))}
        </nav>
      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-6 sm:py-12 pb-28 md:pb-12">
        {left === 0 && !onGames ? <TimeUp onSwitch={switchProfile} name={kid.name} /> : <Outlet />}
      </main>
      <ParentGate open={gate} onOpenChange={setGate} />
    </div>
  );
}

const TimeUp = ({ onSwitch, name }) => (
  <div data-testid="time-up-screen" className="zn-card zn-pop max-w-xl mx-auto p-10 text-center bg-[#7B5CFA] text-white">
    <div className="zn-wiggle inline-grid place-items-center w-24 h-24 rounded-full bg-[#FFD23F] border-[3px] border-[#1B1A2E] mb-6">
      <Moon size={44} color="#1B1A2E" />
    </div>
    <h1 className="text-4xl sm:text-5xl font-bold mb-3">Time for a break, {name}!</h1>
    <p className="text-lg opacity-90 mb-8">You used all your Zunii time for today. Go play, build or draw something awesome, and come back tomorrow!</p>
    <div className="flex flex-col sm:flex-row gap-3 justify-center">
      <button data-testid="time-up-switch-btn" onClick={onSwitch} className="zn-btn bg-white text-[#1B1A2E]"><LogOut size={18} /> Back to profiles</button>
      <NavLink to="/kid/games" data-testid="time-up-games-btn" className="zn-btn bg-[#FFD23F] text-[#1B1A2E]"><Gamepad2 size={18} /> Play games</NavLink>
    </div>
  </div>
);
